import { type CSSProperties, Fragment, type JSX, memo, type RefObject } from "react";
import { Area, AreaChart, LabelList, XAxis, YAxis } from "recharts";
import { chartColor, currentColors } from "../../colors";
import { Legend, LegendLine } from "./stylings";

export type IChartDatum = Record<string, number>;

export interface IProps {
    chartData: IChartDatum[];
    questions: string[];
    areaHeight: number;
    showLabels: boolean;
    chartRef?: RefObject<HTMLDivElement>;
    style?: CSSProperties;
}

const AREA_HEIGHT = 0.5;
const MARGINS = { top: 5, right: 10, bottom: 5, left: 10 };

function makeDomain(idx: number, count: number, height: number): [number, number] {
    const areaHeight = height ?? AREA_HEIGHT;
    const revIdx = count - idx - 1;
    const start = 0 - areaHeight * revIdx;
    const end = areaHeight * (count - revIdx);
    return [start, end];
}

function SpreadChart({ chartData, questions, areaHeight, showLabels = false, chartRef, style }: IProps): JSX.Element {
    return (
        <div style={style} ref={chartRef}>
            <div>
                <AreaChart
                    data={chartData}
                    margin={MARGINS}
                    width="100%"
                    height={questions.length * 50 + 40}
                    responsive
                >
                    <defs>
                        {questions.map((_, idx) => (
                            <linearGradient id={`grad-${idx}`} key={idx} x1={0} y1={0} x2={0} y2={1}>
                                <stop offset="20%" stopColor={chartColor(idx)} stopOpacity={1.0} />
                                <stop offset="100%" stopColor={chartColor(idx)} stopOpacity={0.1} />
                            </linearGradient>
                        ))}
                    </defs>
                    <XAxis />
                    {questions.map((_, idx) => (
                        <YAxis
                            key={idx}
                            yAxisId={`axis-${idx}`}
                            width={0}
                            domain={makeDomain(idx, questions.length, areaHeight)}
                        />
                    ))}
                    {questions.map((question, idx) => (
                        <Area
                            fill={`url(#grad-${idx})`}
                            stroke={chartColor(idx)}
                            yAxisId={`axis-${idx}`}
                            key={idx}
                            dataKey={question}
                            isAnimationActive={false}
                            activeDot={false}
                            type="monotone"
                        >
                            {showLabels && (
                                <LabelList
                                    dataKey={question}
                                    content={(props) => {
                                        const { x, y, index } = props as { x?: number; y?: number; index?: number };
                                        if (x == null || y == null || index == null) {
                                            return null;
                                        }
                                        const datum = chartData[index];
                                        const count = Math.round(datum?.[`${question}__count`] ?? 0);
                                        if (!count) {
                                            return null;
                                        }
                                        return (
                                            <text
                                                x={x}
                                                y={y}
                                                fill={currentColors.text}
                                                fontSize={14}
                                                textAnchor="middle"
                                                dominantBaseline="text-after-edge"
                                            >
                                                {count}
                                            </text>
                                        );
                                    }}
                                />
                            )}
                        </Area>
                    ))}
                </AreaChart>
            </div>
            <Legend>
                {questions.map((question, idx) => (
                    <Fragment key={idx}>
                        <LegendLine $color={chartColor(idx)} />
                        <span style={{ gridColumn: "span 2" }}>{question}</span>
                    </Fragment>
                ))}
            </Legend>
        </div>
    );
}

export default memo(SpreadChart);
